import { ConditionDatesForm, DateRangeSchema } from '@retail/calculus/components';
import { ConditionBinding } from '@retail/calculus/types';
import { usePriceSuggestionTFunction } from '@retail/price-suggestion/i18n';
import { PriceSuggestionTypeWithCount } from '@retail/price-suggestion/types';
import { PriceSuggestionType } from '@retail/products/types';
import { useCallback, useMemo, useState } from 'react';
import { useController, useFormContext } from 'react-hook-form';
import { useConditionActions } from '@retail/calculus/context';
import { EditConditionDialog } from '../../ConditionActionsBase';
import { PriceSuggestionPrioritization } from '@retail/price-suggestion/components';
import { Stack } from '@mui/material';

interface Props {
  open: boolean;
  bindingName: string;
  currentPriority: PriceSuggestionType[];
  priceSuggestionTypes: PriceSuggestionType[];
  conditionBinding: ConditionBinding;
  currentValidFrom?: Date;
  priceSuggestionCount?: PriceSuggestionTypeWithCount[];
  getCustomPriceSuggestionLabel?: (priceSuggestionType: PriceSuggestionType) => string;
  onClose: () => void;
}

export function EditPriceSuggestionConditionDialog({
  open,
  bindingName,
  currentPriority,
  priceSuggestionTypes,
  conditionBinding,
  currentValidFrom,
  priceSuggestionCount,
  getCustomPriceSuggestionLabel,
  onClose,
}: Props) {
  const t = usePriceSuggestionTFunction();
  const { createOrUpdateCondition } = useConditionActions();

  const [updatedPriceSuggestionPriority, setPriceSuggestionPriority] = useState<PriceSuggestionType[]>(currentPriority);

  const { control, reset, formState: { errors }, handleSubmit } = useFormContext<DateRangeSchema>();
  const fromDateController = useController({ name: 'fromDate', control });

  const handleClose = useCallback(() => {
    onClose();
    reset();
  }, [onClose, reset]);

  const submitPriceTypeChange = useCallback(() => {
    createOrUpdateCondition({
      content: { priceSuggestion: { priorities: updatedPriceSuggestionPriority } },
      bindingValue: conditionBinding,
      validFrom: fromDateController.field.value,
    }).then(handleClose);
  }, [conditionBinding, createOrUpdateCondition, fromDateController.field.value, handleClose, updatedPriceSuggestionPriority]);

  const getItemCount = useCallback(
    (priceSuggestionType: PriceSuggestionType) => {
      const itemCount =
        priceSuggestionCount?.find(
          (priceSuggestion) => priceSuggestion.suggestionType === priceSuggestionType
        )?.count + '';
      return t('priceSuggestion.itemCount', { itemCount });
    },
    [priceSuggestionCount, t]
  );

  const noConditionChanges = useMemo(
    () =>
      (updatedPriceSuggestionPriority.every(
        (prio, index) => prio === currentPriority[index]
      ) &&
        currentPriority.every(
          (prio, index) => prio === updatedPriceSuggestionPriority[index]
        )) ||
      updatedPriceSuggestionPriority.length === 0,
    [currentPriority, updatedPriceSuggestionPriority]
  );

  return (
    <EditConditionDialog
      open={open}
      title={t('priceSuggestion.changePriceType', { level: bindingName })}
      dialogContent={
        <Stack gap={2}>
          <PriceSuggestionPrioritization
            priceSuggestionPriority={updatedPriceSuggestionPriority}
            availablePriceSuggestions={priceSuggestionTypes}
            updatePriceSuggestionPrioritazion={setPriceSuggestionPriority}
            getPriceTypeValue={getCustomPriceSuggestionLabel ?? getItemCount}
          />

          <ConditionDatesForm excludeToDate alignment="column" labelAlignment="row" defaultFromDate={currentValidFrom} />
        </Stack>
      }
      isDisabled={noConditionChanges || Object.keys(errors).length > 0}
      onSubmit={handleSubmit(submitPriceTypeChange)}
      onClose={handleClose}
    />


  );
}
